import { useEffect, useState } from 'react';
import { AudioLevelMeter } from '@components/ui/feedback/AudioLevelMeter';
import { useRecordingContext } from '@context/RecordingContext';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  Lock,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Schedule,
  Send as SendIcon,
  Videocam,
  VideocamOff as VideocamOffIcon,
  VolumeUp as VolumeUpIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { differenceInMinutes, format } from 'date-fns';

import { useLocalPreview } from '../hooks/useLocalPreview';

interface TelehealthJoinBoxProps {
  appointmentId?: string;
  nbf?: number | string;
  exp?: number | string;
  onDirectJoin: () => void;
  isJoining: boolean;
  onCopyLink?: () => Promise<void>;
  onSendLink?: () => void;
  isCopyingLink?: boolean;
  isSendingLink?: boolean;
  linkCopied?: boolean;
  onPreviewStopReady?: (stopPreview: (() => void) | null) => void;
  stopPreviewOnDirectJoin?: boolean;
  joinButtonLabel?: string;
  subjectLabel?: string;
}

export function TelehealthJoinBox({
  appointmentId: _appointmentId,
  nbf,
  exp,
  onDirectJoin,
  isJoining,
  onCopyLink,
  onSendLink,
  isCopyingLink,
  isSendingLink,
  linkCopied,
  onPreviewStopReady,
  stopPreviewOnDirectJoin = true,
  joinButtonLabel = 'Join Call to Start Recording',
  subjectLabel = 'Client',
}: TelehealthJoinBoxProps) {
  const theme = useTheme();
  const { audioHealthState, availableMicrophones, selectedMicrophone, setSelectedMicrophone } =
    useRecordingContext();
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [canJoin, setCanJoin] = useState(false);
  const [timeStatus, setTimeStatus] = useState<'early' | 'available' | 'expired'>('available');

  // Device selector menu anchors
  const [micMenuAnchor, setMicMenuAnchor] = useState<HTMLElement | null>(null);
  const [cameraMenuAnchor, setCameraMenuAnchor] = useState<HTMLElement | null>(null);
  const [speakerMenuAnchor, setSpeakerMenuAnchor] = useState<HTMLElement | null>(null);

  const isJoinable = timeStatus === 'available';

  const {
    videoRef,
    isCameraOn,
    isMicOn,
    toggleCamera,
    toggleMic,
    cameras,
    speakers,
    selectedCameraId,
    selectedSpeakerId,
    setCamera,
    setSpeaker,
    stopPreview,
  } = useLocalPreview({ enabled: isJoinable });

  useEffect(() => {
    onPreviewStopReady?.(stopPreview);

    return () => {
      onPreviewStopReady?.(null);
    };
  }, [onPreviewStopReady, stopPreview]);

  // --- Time window logic ---

  const parseTimestamp = (timestamp: number | string | undefined): Date | null => {
    if (!timestamp) return null;
    try {
      if (typeof timestamp === 'number') {
        const date = new Date(timestamp * 1000);
        return isNaN(date.getTime()) ? null : date;
      }
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  const nbfDate = parseTimestamp(nbf);
  const expDate = parseTimestamp(exp);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const now = new Date(currentTime);
    if (!nbfDate && !expDate) {
      setCanJoin(true);
      setTimeStatus('available');
      return;
    }
    if (nbfDate && now < nbfDate) {
      setCanJoin(false);
      setTimeStatus('early');
      return;
    }
    if (expDate && now > expDate) {
      setCanJoin(false);
      setTimeStatus('expired');
      return;
    }
    setCanJoin(true);
    setTimeStatus('available');
  }, [currentTime, nbfDate, expDate]);

  const getTimeUntilAvailable = () => {
    if (!nbfDate) return null;
    const now = new Date(currentTime);
    const minutesUntil = differenceInMinutes(nbfDate, now);
    if (minutesUntil <= 0) return null;
    if (minutesUntil < 60) return `${minutesUntil} minute${minutesUntil !== 1 ? 's' : ''}`;
    const hours = Math.floor(minutesUntil / 60);
    const mins = minutesUntil % 60;
    return `${hours}h ${mins}m`;
  };

  const formatTimestamp = (date: Date | null): string | null => {
    if (!date) return null;
    try {
      if (isNaN(date.getTime())) return null;
      return format(date, 'MMM d, h:mm a');
    } catch {
      return null;
    }
  };

  // --- Derived labels ---

  const selectedCameraLabel =
    cameras.find(c => c.deviceId === selectedCameraId)?.label || cameras[0]?.label || 'Camera';
  const selectedSpeakerLabel =
    speakers.find(s => s.deviceId === selectedSpeakerId)?.label || speakers[0]?.label || 'Speaker';

  // --- Status info for non-joinable states ---

  const getStatusInfo = () => {
    if (timeStatus === 'early') {
      const timeUntil = getTimeUntilAvailable();
      const formattedNbf = formatTimestamp(nbfDate);
      return {
        icon: <Schedule sx={{ color: 'warning.main', fontSize: 28 }} />,
        title: 'Session opens',
        description: formattedNbf
          ? `${formattedNbf}${timeUntil ? ` (${timeUntil})` : ''}`
          : 'Not yet available',
      };
    }
    if (timeStatus === 'expired') {
      const formattedExp = formatTimestamp(expDate);
      return {
        icon: <Lock sx={{ color: 'error.main', fontSize: 28 }} />,
        title: 'Session expired',
        description: formattedExp ?? 'Session has ended',
      };
    }
    return null;
  };

  const statusInfo = getStatusInfo();

  const handleDirectJoin = () => {
    if (stopPreviewOnDirectJoin) {
      stopPreview();
    }
    onDirectJoin();
  };

  // Translucent overlay button style
  const overlayButtonSx = {
    bgcolor: 'rgba(0, 0, 0, 0.35)',
    color: 'common.white',
    backdropFilter: 'blur(4px)',
    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.5)' },
  };

  const mutedOverlayButtonSx = {
    ...overlayButtonSx,
    bgcolor: 'rgba(211, 47, 47, 0.7)',
    '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.85)' },
  };

  // Device selector badge style — matches standard mode mic selector in EncounterViewV2
  const deviceBadgeSx = {
    flex: 1,
    maxWidth: '100%',
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 0.75,
    px: 1.5,
    py: 0.75,
    borderRadius: 2,
    backgroundColor: alpha(theme.palette.grey[500], 0.1),
    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.grey[500], 0.15),
      borderColor: alpha(theme.palette.grey[500], 0.3),
    },
  };

  return (
    <Stack spacing={2}>
      {/* Camera preview (joinable) or session status (waiting/expired) */}
      {isJoinable ? (
        <Box
          sx={{
            width: '100%',
            aspectRatio: '16/9',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'grey.900',
            position: 'relative',
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'scaleX(-1)',
              display: isCameraOn ? 'block' : 'none',
            }}
          />
          {!isCameraOn && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" sx={{ color: 'grey.500' }}>
                Camera off
              </Typography>
            </Box>
          )}
          {/* Translucent mic/camera toggle overlay */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
            }}
          >
            <IconButton
              onClick={toggleMic}
              size="small"
              aria-label={isMicOn ? 'Mute microphone' : 'Unmute microphone'}
              sx={isMicOn ? overlayButtonSx : mutedOverlayButtonSx}
            >
              {isMicOn ? <MicIcon /> : <MicOffIcon />}
            </IconButton>
            <IconButton
              onClick={toggleCamera}
              size="small"
              aria-label={isCameraOn ? 'Turn off camera' : 'Turn on camera'}
              sx={isCameraOn ? overlayButtonSx : mutedOverlayButtonSx}
            >
              {isCameraOn ? <Videocam /> : <VideocamOffIcon />}
            </IconButton>
          </Box>
        </Box>
      ) : (
        statusInfo && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            {statusInfo.icon}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {statusInfo.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {statusInfo.description}
              </Typography>
            </Box>
          </Box>
        )
      )}

      {/* Join button */}
      <Button
        variant="contained"
        onClick={handleDirectJoin}
        disabled={!canJoin || isJoining}
        startIcon={<Videocam />}
        fullWidth
        sx={{
          borderRadius: 2,
          py: 2,
          textTransform: 'none',
        }}
      >
        {joinButtonLabel}
      </Button>

      {/* Waiting-room invite controls stay available until the visit link expires. */}
      {(onCopyLink || onSendLink) && timeStatus !== 'expired' && (
        <Stack direction="row" spacing={1}>
          {onCopyLink && (
            <Button
              size="small"
              variant="outlined"
              startIcon={
                isCopyingLink ? (
                  <CircularProgress size={14} />
                ) : linkCopied ? (
                  <CheckIcon sx={{ fontSize: 16 }} />
                ) : (
                  <CopyIcon sx={{ fontSize: 16 }} />
                )
              }
              onClick={onCopyLink}
              disabled={isCopyingLink || linkCopied}
              aria-label="Copy invite link"
              sx={{ flex: 1, textTransform: 'none' }}
            >
              {linkCopied ? 'Copied!' : 'Copy invite link'}
            </Button>
          )}
          {onSendLink && (
            <Button
              size="small"
              variant="outlined"
              startIcon={
                isSendingLink ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: 16 }} />
              }
              onClick={onSendLink}
              disabled={isSendingLink}
              aria-label={`Send invite to ${subjectLabel}`}
              sx={{ flex: 1, textTransform: 'none' }}
            >
              {`Send invite to ${subjectLabel}`}
            </Button>
          )}
        </Stack>
      )}

      {/* Device selectors — only when camera preview is active (session joinable) */}
      {isJoinable && (
        <Stack spacing={1}>
          {/* Mic selector + audio level meter */}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
            <Box
              onClick={e => setMicMenuAnchor(e.currentTarget)}
              sx={deviceBadgeSx}
              role="button"
              tabIndex={0}
              aria-label="Select microphone"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
                <MicIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedMicrophone?.label || 'Default'}
                </Typography>
              </Box>
              <ArrowDropDownIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
            </Box>
            <AudioLevelMeter healthState={audioHealthState} />
          </Stack>
          <Menu
            anchorEl={micMenuAnchor}
            open={Boolean(micMenuAnchor)}
            onClose={() => setMicMenuAnchor(null)}
            slotProps={{ paper: { sx: { maxWidth: 320 } } }}
          >
            {availableMicrophones?.map(mic => (
              <MenuItem
                key={mic.deviceId}
                selected={mic.deviceId === selectedMicrophone?.deviceId}
                onClick={() => {
                  setSelectedMicrophone(mic);
                  setMicMenuAnchor(null);
                }}
              >
                {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}`}
              </MenuItem>
            ))}
          </Menu>

          {/* Camera selector */}
          <Box
            onClick={e => setCameraMenuAnchor(e.currentTarget)}
            sx={deviceBadgeSx}
            role="button"
            tabIndex={0}
            aria-label="Select camera"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
              <Videocam sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedCameraLabel}
              </Typography>
            </Box>
            <ArrowDropDownIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
          </Box>
          <Menu
            anchorEl={cameraMenuAnchor}
            open={Boolean(cameraMenuAnchor)}
            onClose={() => setCameraMenuAnchor(null)}
            slotProps={{ paper: { sx: { maxWidth: 320 } } }}
          >
            {cameras.map(cam => (
              <MenuItem
                key={cam.deviceId}
                selected={
                  cam.deviceId === selectedCameraId || (!selectedCameraId && cam === cameras[0])
                }
                onClick={() => {
                  setCamera(cam.deviceId);
                  setCameraMenuAnchor(null);
                }}
              >
                {cam.label || `Camera ${cam.deviceId.slice(0, 8)}`}
              </MenuItem>
            ))}
          </Menu>

          {/* Speaker selector */}
          <Box
            onClick={e => setSpeakerMenuAnchor(e.currentTarget)}
            sx={deviceBadgeSx}
            role="button"
            tabIndex={0}
            aria-label="Select speaker"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
              <VolumeUpIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedSpeakerLabel}
              </Typography>
            </Box>
            <ArrowDropDownIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
          </Box>
          <Menu
            anchorEl={speakerMenuAnchor}
            open={Boolean(speakerMenuAnchor)}
            onClose={() => setSpeakerMenuAnchor(null)}
            slotProps={{ paper: { sx: { maxWidth: 320 } } }}
          >
            {speakers.map(spk => (
              <MenuItem
                key={spk.deviceId}
                selected={
                  spk.deviceId === selectedSpeakerId || (!selectedSpeakerId && spk === speakers[0])
                }
                onClick={() => {
                  setSpeaker(spk.deviceId);
                  setSpeakerMenuAnchor(null);
                }}
              >
                {spk.label || `Speaker ${spk.deviceId.slice(0, 8)}`}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      )}
    </Stack>
  );
}
